import React, { useState, useEffect } from 'react';
import { Card, Row, Col, message, Tag, Button, Spin, Alert } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import api from '../services/api';

const PlansList = ({ 
  plans = [], 
  user = {}, 
  onPlanUpdate = () => {} 
}) => {
 
  const [, setLoading] = useState(false);
  const [activePlans, setActivePlans] = useState([]);
  const [paymentProcessing, setPaymentProcessing] = useState(null);
  // Fetch user's active plans on component mount
useEffect(() => {
  fetchActivePlans();
}, []);

// Loads the Razorpay Checkout script if it isn't already on the page
// (it's also included in public/index.html, this is just a safety net).
const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });




  const fetchActivePlans = async () => {
    try {
      const response = await api.get('/user/active-plans');
      setActivePlans(response.data.plans || []);
    } catch (error) {
      console.error('Error fetching active plans:', error);
      setActivePlans([]);
    }
  };

  const handlePurchase = async (plan) => {
    if (!plan) return;

    setLoading(true);
    setPaymentProcessing(plan.id);

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded || !window.Razorpay) {
        message.error({
          content: 'Unable to load the payment gateway. Please check your connection and try again.',
          duration: 6
        });
        setPaymentProcessing(null);
        setLoading(false);
        return;
      }

      // Ask our backend to create a Razorpay order for this plan
      const response = await api.post('/user/initiate-payment', {
        planId: plan.id
      });

      if (!response.data.success) {
        message.error({
          content: response.data.message || 'Failed to initiate payment',
          duration: 5
        });
        setPaymentProcessing(null);
        setLoading(false);
        return;
      }

      const { orderId, amount, currency, keyId, transactionId, name, email, contact } = response.data.data;

      const options = {
        key: keyId,
        amount,
        currency,
        name: 'BusinessMateWorkflow',
        description: `${plan.plan_name} Plan`,
        order_id: orderId,
        prefill: { name, email, contact },
        theme: { color: '#413AA4' },
        handler: async (paymentResult) => {
          try {
            const verifyRes = await api.post('/user/verify-payment', {
              razorpay_order_id: paymentResult.razorpay_order_id,
              razorpay_payment_id: paymentResult.razorpay_payment_id,
              razorpay_signature: paymentResult.razorpay_signature,
              transactionId
            });

            if (verifyRes.data.success) {
              message.success({
                content: `Payment successful! Your ${plan.plan_name} plan has been activated.`,
                duration: 6,
                key: `payment-${transactionId}`
              });
              fetchActivePlans();
              if (onPlanUpdate) onPlanUpdate();
            } else {
              message.error({
                content: verifyRes.data.message || 'Payment verification failed. Please contact support.',
                duration: 8,
                key: `payment-${transactionId}`
              });
            }
          } catch (verifyError) {
            console.error('Payment verification error:', verifyError);
            message.error({
              content: 'We could not confirm your payment automatically. If money was deducted, please contact support with your transaction ID.',
              duration: 10,
              key: `payment-${transactionId}`
            });
          } finally {
            setPaymentProcessing(null);
          }
        },
        modal: {
          ondismiss: () => {
            message.info({ content: 'Payment cancelled.', duration: 4 });
            setPaymentProcessing(null);
          }
        }
      };

      const razorpayCheckout = new window.Razorpay(options);
      razorpayCheckout.on('payment.failed', (resp) => {
        message.error({
          content: `Payment failed: ${resp.error?.description || 'Please try again.'}`,
          duration: 8
        });
        setPaymentProcessing(null);
      });
      razorpayCheckout.open();

    } catch (error) {
      console.error('Payment initiation error:', error);

      let errorMsg = 'Failed to initiate payment. Please try again.';

      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.response?.status === 404) {
        errorMsg = 'Plan not found or inactive.';
      } else if (error.response?.status === 400) {
        errorMsg = error.response.data?.message || 'Invalid request. Please check your plan selection.';
      } else if (error.response?.status >= 500) {
        errorMsg = 'Server error. Please try again later.';
      }

      message.error({
        content: errorMsg,
        duration: 6
      });
      setPaymentProcessing(null);
    } finally {
      setLoading(false);
    }
  };

  // Check if plan is already active
  const isPlanActive = (planId) => {
    return activePlans.some(plan => plan.plan_id === planId);
  };

  // Get expiry date for active plan
  const getExpiryDate = (planId) => {
    const plan = activePlans.find(p => p.plan_id === planId);
    return plan ? new Date(plan.expiry_date).toLocaleDateString() : null;
  };

  // Get active plan details
  const getActivePlanDetails = (planId) => {
    return activePlans.find(p => p.plan_id === planId);
  };

  const formatPrice = (price) => {
    return typeof price === 'string' ? parseFloat(price) : price;
  };

  return (
    <div className="plans-container">
      <div style={{ marginBottom: 20 }}>
        <h3>Available Plans for {user.type || 'User'}</h3>
        
        {activePlans.length > 0 && (
          <Alert
            message={`You have ${activePlans.length} active plan${activePlans.length > 1 ? 's' : ''}`}
            type="info"
            showIcon
            icon={<CheckCircleOutlined />}
            style={{ marginBottom: 16 }}
          />
        )}
      </div>
      
      {plans.length === 0 ? (
        <Alert
          message="No Plans Available"
          description="No plans are currently available for your user type."
          type="info"
          showIcon
        />
      ) : (
        <Row gutter={[16, 16]}>
          {plans.map(plan => {
            const isActive = isPlanActive(plan.id);
            const activePlanDetails = getActivePlanDetails(plan.id);
            const isProcessing = paymentProcessing === plan.id;
            
            return (
              <Col key={plan.id} xs={24} sm={12} lg={8}>
                <Card 
                  title={
                    <div>
                      {plan.plan_name || 'Unnamed Plan'}
                      {isActive && (
                        <Tag color="success" style={{ marginLeft: 8 }}>
                          ACTIVE
                        </Tag>
                      )}
                    </div>
                  }
                  style={{ 
                    background: isActive ? '#f6ffed' : '#f9f9f9',
                    minHeight: '400px'
                  }}
                >
                  {/* Active Plan Status */}
                  {isActive && activePlanDetails && (
                    <div style={{ marginBottom: 16 }}>
                      <Tag color="green">
                        Active until {getExpiryDate(plan.id)}
                      </Tag>
                      <br />
                      <small style={{ color: '#52c41a' }}>
                        Purchased on {new Date(activePlanDetails.purchase_date).toLocaleDateString()}
                      </small>
                    </div>
                  )}
                  
                  {/* Price Display */}
                  <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '16px', color: '#1890ff' }}>
                    ₹{formatPrice(plan.price)} 
                    <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666' }}>
                      / {plan.validity_days} days
                    </span>
                  </div>
                  
                  {/* Plan Description */}
                  <div style={{ marginBottom: '16px', minHeight: '60px' }}>
                    {plan.content || plan.description || 'No description available.'}
                  </div>
                  
                  {/* Plan Meta Information */}
                  <div className="plan-meta" style={{ fontSize: '12px', color: '#666', marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span>Status:</span>
                      <span style={{ fontWeight: 500 }}>{plan.status || 'N/A'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span>Validity:</span>
                      <span style={{ fontWeight: 500 }}>{plan.validity_days} days</span>
                    </div>
          	           {/* {plan.expiration_date && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Available until:</span>
                        <span style={{ fontWeight: 500 }}>
                          {new Date(plan.expiration_date).toLocaleDateString()}
                        </span>
                      </div>
                    )} */}
                  </div>

                  {/* Action Button */}
                  <div style={{ textAlign: 'center', marginTop: 'auto' }}>
                    {isActive ? (
                      <Button 
                        type="default" 
                        disabled 
                        size="large"
                        style={{ width: '100%', border: 'none', background: 'transparent' }}
                      >
                        ✓ Active Plan
                      </Button>
                    ) : (
                      <Button 
                        type="primary" 
                        size="large"
                        style={{ width: '100%' }}
                        onClick={() => handlePurchase(plan)}
                        loading={isProcessing}
                        disabled={isProcessing}
                      >
                        {isProcessing ? (
                          <span>
                            <Spin size="small" style={{ marginRight: 8 }} />
                            Processing...
                          </span>
                        ) : (
                          `Pay Now`
                        )}
                      </Button>
                    )}
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </div>
  );
};

PlansList.propTypes = {
  plans: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      plan_name: PropTypes.string.isRequired,
      validity_days: PropTypes.number.isRequired,
      price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      content: PropTypes.string,
      description: PropTypes.string,
      expiration_date: PropTypes.string,
      status: PropTypes.string
    })
  ),
  user: PropTypes.object,
  onPlanUpdate: PropTypes.func
};

PlansList.defaultProps = {
  plans: [],
  user: {},
  onPlanUpdate: () => {}
};

export default PlansList;